import { Avatar, Breadcrumbs, Button, Card, CardContent, CircularProgress, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import HomeIcon from '@material-ui/icons/Home';
import PersonIcon from '@material-ui/icons/Person';
import { Alert, AlertTitle } from '@material-ui/lab';
import axios from 'axios';
import { FunctionComponent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AppShell } from '../components/molecules/app-shell/AppShell';
import { useStore } from '../store';
import { ApiResponse } from '../types';

const useStyles = makeStyles((theme) => ({
    controlPaper: {
        width: "100%",
        padding: "8px",
    },
    link: {
        display: 'flex',
        textDecoration: 'none',
    },
    icon: {
        marginRight: theme.spacing(0.5),
        width: 20,
        height: 20,
    },
    fieldLabel: {
        fontWeight: 600,
        color: theme.palette.text.secondary
    },
    fieldValue: {
        marginBottom: theme.spacing(1.5),
    },
    avatar: {
        width: theme.spacing(12),
        height: theme.spacing(12),
        margin: 'auto',
        backgroundColor: theme.palette.primary.main,
    },
    centerButton: {
        justifyContent: 'center',
        display: 'flex',
    },

}))

type PensionerProfilePageProps = {
}

type PensionerApiData = {
    name: string,
    spouseName: string,
    pensionerAddr: string,
    dobSpouse: string,
    dtRet: string,
    categoryPensioner: string,
    lastPenMonth: string,
    pensionerType: string,
    aadharNo: string,
    panId: string,
    contactNo: string,
    emailId: string,
    bankAccNum: string,
}

type PensionerProfileState = {
    loading: boolean,
    error: string,
    profile: Array<PensionerApiData>
}

export const PensionerProfilePage: FunctionComponent<PensionerProfilePageProps> = () => {
    const classes = useStyles()
    const store = useStore()
    const [pensionerProfile, setPensionerProfile] = useState<PensionerProfileState>({ loading: true, error: '', profile: [] });

    useEffect(() => {
        fetchProfile()
    }, []);

    const fetchProfile = async () => {
        axios.get<ApiResponse<{ profile: PensionerApiData[] }>>(`api/pensioner/profile-details`, {
            headers: {
                'Authorization': `Bearer ${store.auth.jwt}`, 'JWT': `Bearer ${store.auth.jwt}`
            }
        })
            .then(
                ({ data: resp }) => {
                    if (resp.status === "success") {
                        const profile: PensionerApiData[] = resp.data.profile;
                        setPensionerProfile({ loading: false, error: '', profile: profile })
                    } else {
                        setPensionerProfile({ loading: false, error: resp.message, profile: [] })
                    }
                },
                () => { setPensionerProfile({ loading: false, error: "Network Error", profile: [] }) }
            )
    }

    return (
        <AppShell>
            <Grid container
                spacing={3}
                direction="column"
                justify="space-around"
                alignItems="stretch">
                <Grid item>
                    <Card className={classes.controlPaper}>
                        <CardContent>
                            <Grid container spacing={3} direction="column" justify="space-around"
                                alignItems="stretch">
                                <Grid item>
                                    <Breadcrumbs>
                                        <Link to="/" className={classes.link}>
                                            <Typography gutterBottom variant="h5" component="h2">
                                                <HomeIcon className={classes.icon} />
                                                Home
                                            </Typography>
                                        </Link>
                                        <Typography gutterBottom variant="h5" component="h2">
                                            Profile Details (ব্যক্তিগত পরিচিতি)
                                        </Typography>
                                    </Breadcrumbs>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
                < Grid item>
                    {
                        pensionerProfile.loading && <CircularProgress size={68} color="secondary" />
                    }
                    {
                        !pensionerProfile.loading && pensionerProfile.error &&
                        <>
                            <Alert severity="error">
                                <AlertTitle>Error</AlertTitle>
                                {pensionerProfile.error}
                                <Button variant="outlined" color="primary" onClick={fetchProfile}>
                                    Retry
                                </Button>
                            </Alert>
                        </>
                    }
                    {
                        !pensionerProfile.loading && !pensionerProfile.error && pensionerProfile.profile.length === 0 &&
                        <>
                            <Alert severity="warning">
                                <AlertTitle>Warning</AlertTitle>
                                Pensioner Profile Details not found!!
                            </Alert>
                        </>
                    }
                </Grid>
                {!pensionerProfile.loading && !pensionerProfile.error && pensionerProfile.profile?.length > 0 &&
                    (
                        < Grid item>
                            <Card elevation={3}>
                                <CardContent>
                                    <Grid container spacing={3}>
                                        <Grid item sm={4} xs={12} style={{ textAlign: 'center' }}>
                                            <Avatar alt={pensionerProfile?.profile[0]?.name} src={pensionerProfile?.profile[0]?.name} className={classes.avatar}>
                                                <PersonIcon fontSize='large' />
                                            </Avatar>
                                            <Typography variant='h6' gutterBottom>
                                                {pensionerProfile?.profile[0]?.name}
                                            </Typography>
                                            <Typography variant='body2' color='textSecondary'>
                                                <b>{pensionerProfile?.profile[0]?.categoryPensioner}
                                                    ({pensionerProfile?.profile[0]?.pensionerType})</b>
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={8}>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={6}>
                                                    <Typography className={classes.fieldLabel}>
                                                        Spouse Name (স্ত্রী / স্বামীর নাম)
                                                    </Typography>
                                                    <Typography className={classes.fieldValue}>
                                                        {pensionerProfile?.profile[0]?.spouseName ? pensionerProfile?.profile[0]?.spouseName : "NA"}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Typography className={classes.fieldLabel}>
                                                        Bank Account Number (ব্যাঙ্ক অ্যাকাউন্ট নম্বর)
                                                    </Typography>
                                                    <Typography className={classes.fieldValue}>
                                                        {pensionerProfile?.profile[0]?.bankAccNum ? pensionerProfile?.profile[0]?.bankAccNum : "NA"}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Typography className={classes.fieldLabel}>
                                                        Spouse Date of Birth (স্ত্রী / স্বামীর জন্মতারিখ)
                                                    </Typography>
                                                    <Typography className={classes.fieldValue}>
                                                        {pensionerProfile?.profile[0]?.dobSpouse ? pensionerProfile?.profile[0]?.dobSpouse : "NA"}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Typography className={classes.fieldLabel}>
                                                        Retirement Date (অবসরের তারিখ)
                                                    </Typography>
                                                    <Typography className={classes.fieldValue}>
                                                        {pensionerProfile?.profile[0]?.dtRet ? pensionerProfile?.profile[0]?.dtRet : "NA"}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Typography className={classes.fieldLabel}>
                                                        Last Pension Month (পেনশন প্রদানের শেষ মাস)
                                                    </Typography>
                                                    <Typography className={classes.fieldValue}>
                                                        {pensionerProfile?.profile[0]?.lastPenMonth ? pensionerProfile?.profile[0]?.lastPenMonth : "NA"}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Typography className={classes.fieldLabel}>
                                                        AADHAR Number (আধার নম্বর)
                                                    </Typography>
                                                    <Typography className={classes.fieldValue}>
                                                        {pensionerProfile?.profile[0]?.aadharNo ? pensionerProfile?.profile[0]?.aadharNo : "NA"}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Typography className={classes.fieldLabel}>
                                                        PAN Number (প্যান নম্বর)
                                                    </Typography>
                                                    <Typography className={classes.fieldValue}>
                                                        {pensionerProfile?.profile[0]?.panId ? pensionerProfile?.profile[0]?.panId : "NA"}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Typography className={classes.fieldLabel}>
                                                        Mobile no./Telephone no. (মোবাইল নম্বর/টেলিফোন নম্বর)
                                                    </Typography>
                                                    <Typography className={classes.fieldValue}>
                                                        {pensionerProfile?.profile[0]?.contactNo ? pensionerProfile?.profile[0]?.contactNo : "NA"}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Typography className={classes.fieldLabel}>
                                                        Email (ইমেইল)
                                                    </Typography>
                                                    <Typography className={classes.fieldValue}>
                                                        {pensionerProfile?.profile[0]?.emailId ? pensionerProfile?.profile[0]?.emailId.toLocaleLowerCase() : "NA"}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Typography className={classes.fieldLabel}>
                                                        Residental Address (বাড়ির ঠিকানা)
                                                    </Typography>
                                                    <Typography className={classes.fieldValue}>
                                                        {pensionerProfile?.profile[0]?.pensionerAddr ? pensionerProfile?.profile[0]?.pensionerAddr : "NA"}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}
                <Grid item>
                    <Alert severity={'info'} style={{ textAlign: "justify" }}>
                        <AlertTitle>
                            To update profile details, please contact pension cell.<br />
                            <b>ব্যক্তিগত তথ্য সংশোধনের জন্য, দয়া করে পেনশন সেলের সাথে যোগাযোগ করুন।</b>
                        </AlertTitle>
                    </Alert>
                </Grid>
                <Grid item className={classes.centerButton}>
                    <Link to="/" className={classes.link}>
                        <Button variant='contained' color='primary'>
                            Back (পিছনে)
                        </Button>
                    </Link>
                </Grid>
            </Grid>
        </AppShell >
    )
}